/**
 * The small pieces every screen uses.
 *
 * Each carries the semantics rather than just the styling — `Status` knows which run
 * states mean trouble, `RelativeTime` knows a fresh timestamp reads better as "2m ago"
 * and an old one as a date.
 */

import type { ReactNode } from "react";

import type { RunStatus } from "@/api/endpoints";

// ---------------------------------------------------------------------------- notice

type Tone = "info" | "success" | "warning" | "danger";

export function Notice({
  tone = "info",
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : undefined}>
      <div>
        {title && <strong>{title}</strong>}
        {title && children ? <div style={{ marginTop: 2 }}>{children}</div> : children}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------- badge

export function Badge({
  tone,
  children,
  live = false,
}: {
  tone?: Tone | "accent";
  children: ReactNode;
  live?: boolean;
}) {
  return (
    <span className={tone ? `badge badge--${tone}` : "badge"}>
      {live && <span className="badge__dot badge__dot--live" />}
      {children}
    </span>
  );
}

/** Run statuses, with the tone each one deserves. */
const RUN_TONES: Record<RunStatus, { tone: Tone | "accent" | undefined; label: string; live?: boolean }> = {
  queued: { tone: undefined, label: "Queued" },
  building: { tone: "accent", label: "Building", live: true },
  running: { tone: "accent", label: "Running", live: true },
  succeeded: { tone: "success", label: "Succeeded" },
  succeeded_with_warnings: { tone: "warning", label: "Warnings" },
  validation_failed: { tone: "danger", label: "Validation failed" },
  failed: { tone: "danger", label: "Failed" },
  timed_out: { tone: "danger", label: "Timed out" },
  cancelled: { tone: undefined, label: "Cancelled" },
  skipped: { tone: undefined, label: "Skipped" },
};

export function Status({ status }: { status: RunStatus }) {
  const meta = RUN_TONES[status] ?? { tone: undefined, label: status };
  return (
    <Badge tone={meta.tone} live={meta.live}>
      {meta.label}
    </Badge>
  );
}

export function isTerminal(status: RunStatus): boolean {
  return !["queued", "building", "running"].includes(status);
}

// ------------------------------------------------------------------------------ time

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const ABSOLUTE = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const seconds = Math.round((then - Date.now()) / 1000);
  const magnitude = Math.abs(seconds);

  // Past about a week, a date is more useful than a count of days.
  if (magnitude > 604_800) return ABSOLUTE.format(then);
  if (magnitude < 60) return RELATIVE.format(Math.round(seconds), "second");
  if (magnitude < 3_600) return RELATIVE.format(Math.round(seconds / 60), "minute");
  if (magnitude < 86_400) return RELATIVE.format(Math.round(seconds / 3_600), "hour");
  return RELATIVE.format(Math.round(seconds / 86_400), "day");
}

export function RelativeTime({ iso }: { iso: string | null | undefined }) {
  if (!iso) return <span className="faint">—</span>;
  return (
    <time dateTime={iso} title={ABSOLUTE.format(new Date(iso))}>
      {formatRelative(iso)}
    </time>
  );
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

// ------------------------------------------------------------------------ page states

export function Loading({ rows = 3, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <div className="stack" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="skeleton"
          style={{ width: `${100 - index * 12}%`, height: index === 0 ? 20 : 14 }}
        />
      ))}
    </div>
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {children && <div style={{ maxWidth: 460, margin: "0 auto 14px" }}>{children}</div>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <Notice tone="danger" title="That did not work">
      <div>{error.message}</div>
      {onRetry && (
        <button type="button" className="button button--small" style={{ marginTop: 10 }} onClick={onRetry}>
          Try again
        </button>
      )}
    </Notice>
  );
}

// ----------------------------------------------------------------------------- field

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string | null;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="field__required" aria-label="required">
            *
          </span>
        )}
      </label>
      {hint && <div className="field__hint">{hint}</div>}
      {children}
      {error && (
        <div className="field__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
